import { useState, useEffect } from 'react';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// components
import Dialog from '../../../components/dialog';
import { Label, ButtonField, TextField, ButtonFields, SelectField } from '../../../components/hook-form';
// @form
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// utils
import { REGEXP_SPECIAL, REGEXP_SERIAL_NUMBER } from '../../../utils/regexp';
import { timezones } from '../../../utils/format';
// styles
import { DevicesContent, Tabs, Message, ButtonWrapper, PrimaryButton, TertiaryButton, Radio, GeneralContent, Switch } from '../../../styles';
// apis
import { getDeviceInfo, existsSerialNumber, existsNumber, existsLocation, updateGeneral, updatePower, updateAction } from '../../../apis';

// ----------------------------------------------------------------------

interface Props {
  row: any;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  handleReset: () => void;
};

const tabs = [{ value: 'general', name: '일반' }, { value: 'power', name: '전원' }, { value: 'action', name: '동작' }];

// ----------------------------------------------------------------------

export default function SettingDialog({ row, setVisible, handleReset }: Props) {
  const mobile = useResponsive('down', 'md');
  
  const handleClose = () => setVisible(false);

  const [ info, setInfo ] = useState({general: null, power: null, action: null});

  const [ message, setMessage ] = useState(null);

  useEffect(() => {
    ( async () =>
      await getDeviceInfo(setInfo, row.iotDeviceId)
        .catch(() => {
          return setMessage('정보를 불러올 수 없습니다. 나중에 다시 시도해 주세요.');
        })
    )();
  }, []);

  const [ tab, setTab ] = useState('general');

  return (
    <Dialog
      title='장비 설정'
      handleClose={handleClose}
    >
      <DevicesContent className={mobile ? 'mobile' : ''}>

        { message ? (
          <>
            <p style={{'fontSize': '14px', 'textAlign': 'center'}}>정보를 불러올 수 없습니다. 나중에 다시 시도해 주세요.</p>
            <PrimaryButton onClick={() => handleClose()}>확인</PrimaryButton>
          </>
        ) : (
          <>
            <p>{ row.iotGroupName } - { row.location }</p>
            
            <Tabs>
              { tabs.map((item) => (
                <button key={item.value} onClick={() => setTab(item.value)} className={tab === item.value ? 'active' : ''}>{item.name}</button>
              ))}
            </Tabs>
    
            { info.general &&
              tab === 'general' ? (
                <General info={info.general} iotGroupId={row.iotGroupId} iotDeviceId={row.iotDeviceId} handleClose={handleClose} handleReset={handleReset} />
              ) : tab === 'power' ? (
                <Power info={info.power} iotDeviceId={row.iotDeviceId} handleClose={handleClose} handleReset={handleReset} />
              ) : (
                <Action info={info.action} iotDeviceId={row.iotDeviceId} handleClose={handleClose} handleReset={handleReset} />
              )
            }
          </>
        )}

      </DevicesContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------

function General({ info, iotGroupId, iotDeviceId, handleClose, handleReset }: any) {

  const [ serial, setSerial ] = useState(info.serialNumber);
  const [ enabled, setEnabled ] = useState(info.enabled);
  const [ number, setNumber ] = useState(info.number);
  const [ location, setLocation ] = useState(info.location);

  const [ visibleSerial, setVisibleSerial ] = useState(false);
  const [ visibleNumber, setVisibleNumber ] = useState(false);
  const [ visibleLocation, setVisibleLocation ] = useState(false);
  const closeSerial = () => setVisibleSerial(false);
  const closeNumber = () => setVisibleNumber(false);
  const closeLocation = () => setVisibleLocation(false);

  const [ message, setMessage ] = useState(null);

  const handleSubmit = () => {
    const modifySerialNumber = info.serialNumber !== serial ? true : false;
    updateGeneral(iotDeviceId, iotGroupId, enabled, info.number === number ? null : number, info.location === location ? null : location, modifySerialNumber, modifySerialNumber ? serial : null)
      .then(() => {
        handleReset();
        handleClose();
      })
      .catch(() => setMessage('정보를 수정할 수 없습니다. 나중에 다시 시도해 주세요.'));
  };

  return (
    <GeneralContent>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        height: '465px'
      }}>
        <div>
          <Label>일련 번호</Label>
          <ButtonFields>
            <input type='text' value={serial} disabled />
            <TertiaryButton type='button' onClick={() => setVisibleSerial(true)}>수정</TertiaryButton>
          </ButtonFields>
        </div>

        <div>
          <Label><span>* </span>장비 상태</Label>
          <Radio>
            <label>
              <input type='radio' name='enabled' checked={enabled ? true : false} onChange={() => setEnabled(true)} /> 사용
            </label>
            <label>
              <input type='radio' name='enabled' checked={!enabled ? true : false} onChange={() => setEnabled(false)} /> 사용 안 함
            </label>
          </Radio>
        </div>

        <div>
          <Label>장비 번호</Label>
          <ButtonFields>
            <input type='text' value={number} disabled />
            <TertiaryButton type='button' onClick={() => setVisibleNumber(true)} disabled={!enabled ? true : false}>수정</TertiaryButton>
          </ButtonFields>
        </div>

        <div>
          <Label>설치 위치</Label>
          <ButtonFields>
            <input type='text' value={location} disabled />
            <TertiaryButton type='button' onClick={() => setVisibleLocation(true)} disabled={!enabled ? true : false}>수정</TertiaryButton>
          </ButtonFields>
        </div>

        { visibleNumber &&
          <Number number={number} setNumber={setNumber} iotGroupId={iotGroupId} handleClose={closeNumber} />
        }

        { visibleLocation &&
          <Location location={location} setLocation={setLocation} iotGroupId={iotGroupId} handleClose={closeLocation} />
        }

        { visibleSerial &&
          <Serial serial={serial} setSerial={setSerial} handleClose={closeSerial} />
        }
      </div>

      <Message $visible={message ? true : false}>{ message }</Message>

      <ButtonWrapper>
        <PrimaryButton onClick={() => handleSubmit()}>저장</PrimaryButton>
        <TertiaryButton type='button' onClick={() => handleClose()}>취소</TertiaryButton>
      </ButtonWrapper>
    </GeneralContent>
  );
}

const serialSchema = yup.object().shape({
  serial: yup.string()
    .required()
    .trim()
    .matches(REGEXP_SERIAL_NUMBER, '10자의 영문 대문자, 숫자를 사용해 주세요.')
});

function Serial({ serial, setSerial, handleClose }: any) {
  const {
    control,
    trigger,
    getValues,
    handleSubmit
  } = useForm({
    resolver: yupResolver(serialSchema),
    defaultValues: {
      serial: serial
    }
  });

  const [ checkSerial, setCheckSerial ] = useState({result: null, message: null});
  const [ checkedSerial, setCheckedSerial ] = useState(null);

  const handleSerial = async () => {
    trigger();
    const inputSerial = getValues('serial');
    if ( REGEXP_SERIAL_NUMBER.test(inputSerial) ) {
      await existsSerialNumber(inputSerial)
        .then((response) => {
          setCheckSerial({result: response, message: response ? '이미 등록된 일련 번호입니다.' : '사용하실 수 있는 일련 번호입니다.'});
          setCheckedSerial(inputSerial);
        })
        .catch(() => {
          setCheckSerial({result: null, message: '나중에 다시 시도해 주세요.'});
        });
    }
  };

  const onSubmit = async (data: { serial?: string }) => {
    if ( checkSerial.result === null || checkedSerial !== data.serial ) {
      setCheckSerial({result: null, message: '중복 체크 후 진행하실 수 있습니다.'});
    } else if ( checkSerial.result === false && checkedSerial === data.serial ) {
      setSerial(data.serial);
      handleClose();
    }
  };


  return (
    <Dialog
      title='일련 번호 수정'
      handleClose={handleClose}
    >
      <form onSubmit={ handleSubmit(onSubmit) }>
        <ButtonField
          name='serial'
          label='일련 번호'
          essential={true}
          type='text'
          control={control}
          exists={checkSerial}
          action={
            <TertiaryButton type='button' onClick={() => handleSerial()}>중복 체크</TertiaryButton>
          }
        />

        <ButtonWrapper>
          <PrimaryButton>수정</PrimaryButton>
          <TertiaryButton type='button' onClick={() => handleClose()}>취소</TertiaryButton>
        </ButtonWrapper>
      </form>
    </Dialog>
  );
}

const numberSchema = yup.object().shape({
  number: yup.number()
    .required()
    .min(1, '1이상 정수를 입력해 주세요.')
    .integer('1이상 정수를 입력해 주세요.')
});

function Number({ number, setNumber, iotGroupId, handleClose }: any) {
  const {
    control,
    trigger,
    getValues,
    handleSubmit
  } = useForm({
    resolver: yupResolver(numberSchema),
    defaultValues: {
      number: number
    }
  });

  const [ checkNumber, setCheckNumber ] = useState({result: null, message: null});
  const [ checkedNumber, setCheckedNumber ] = useState(null);

  const handleNumber = async () => {
    trigger();
    const inputNumber = getValues('number');
    if ( inputNumber > 0 && inputNumber % 1 === 0 ) {
      await existsNumber(iotGroupId, inputNumber)
        .then((response) => {
          setCheckNumber({result: response, message: response ? '이미 등록된 장비 번호입니다.' : '사용하실 수 있는 장비 번호입니다.'});
          setCheckedNumber(inputNumber);
        })
        .catch(() => {
          setCheckNumber({result: null, message: '나중에 다시 시도해 주세요.'});
        });
    }
  };

  const onSubmit = async (data: { number?: number }) => {
    if ( checkNumber.result === null || checkedNumber !== String(data.number) ) {
      setCheckNumber({result: null, message: '중복 체크 후 진행하실 수 있습니다.'});
    } else if ( checkNumber.result === false && checkedNumber === String(data.number) ) {
      setNumber(data.number);
      handleClose();
    }
  };


  return (
    <Dialog
      title='장비 번호 수정'
      handleClose={handleClose}
    >
      <form onSubmit={ handleSubmit(onSubmit) } noValidate>
        <ButtonField
          type='number'
          name='number'
          label='장비 번호'
          essential={true}
          control={control}
          exists={checkNumber}
          action={
            <TertiaryButton type='button' onClick={() => handleNumber()}>중복 체크</TertiaryButton>
          }
        />

        <ButtonWrapper>
          <PrimaryButton>수정</PrimaryButton>
          <TertiaryButton type='button' onClick={() => handleClose()}>취소</TertiaryButton>
        </ButtonWrapper>
      </form>
    </Dialog>
  );
}

const locationSchema = yup.object().shape({
  location: yup.string()
    .required()
    .trim()
    .min(2, '2~30자 사이 한글, 영문 대소문자, 숫자, 특수기호!@&()-_+[]{}. 를 사용해 주세요.')
    .max(30, '2~30자 사이 한글, 영문 대소문자, 숫자, 특수기호!@&()-_+[]{}. 를 사용해 주세요.')
    .matches(REGEXP_SPECIAL, '2~30자 사이 한글, 영문 대소문자, 숫자, 특수기호!@&()-_+[]{}. 를 사용해 주세요.')
});

function Location({ location, setLocation, iotGroupId, handleClose }: any) {
  const {
    control,
    trigger,
    getValues,
    handleSubmit
  } = useForm({
    resolver: yupResolver(locationSchema),
    defaultValues: {
      location: location
    }
  });

  const [ checkLocation, setCheckLocation ] = useState({result: null, message: null});
  const [ checkedLocation, setCheckedLocation ] = useState(null);

  const handleLocation = async () => {
    trigger();
    const inputLocation = getValues('location');
    if ( inputLocation.length > 1 && inputLocation.length < 31 && REGEXP_SPECIAL.test(inputLocation) ) {
      await existsLocation(iotGroupId, inputLocation)
        .then((response) => {
          setCheckLocation({result: response, message: response ? '이미 등록된 설치 위치입니다.' : '사용하실 수 있는 설치 위치입니다.'});
          setCheckedLocation(inputLocation);
        })
        .catch(() => {
          setCheckLocation({result: null, message: '나중에 다시 시도해 주세요.'});
        });
    }
  };

  const onSubmit = async (data: { location?: string }) => {
    if ( checkLocation.result === null || checkedLocation !== data.location ) {
      setCheckLocation({result: null, message: '중복 체크 후 진행하실 수 있습니다.'});
    } else if ( checkLocation.result === false && checkedLocation === data.location ) {
      setLocation(data.location);
      handleClose();
    }
  };


  return (
    <Dialog
      title='장비 번호 수정'
      handleClose={handleClose}
    >
      <form onSubmit={ handleSubmit(onSubmit) }>
        <ButtonField
          name='location'
          label='설치 위치'
          essential={true}
          type='text'
          control={control}
          exists={checkLocation}
          action={
            <TertiaryButton type='button' onClick={() => handleLocation()}>중복 체크</TertiaryButton>
          }
          maxLength={30}
        />

        <ButtonWrapper>
          <PrimaryButton>수정</PrimaryButton>
          <TertiaryButton type='button' onClick={() => handleClose()}>취소</TertiaryButton>
        </ButtonWrapper>
      </form>
    </Dialog>
  );
}

// ----------------------------------------------------------------------

const powerSchema = yup.object().shape({
  lowBatteryLevel: yup.number()
    .min(1, '1~100 사이 정수를 입력해 주세요.')
    .max(100, '1~100 사이 정수를 입력해 주세요.')
    .integer('1~100 사이 정수를 입력해 주세요.')
});

function Power({ info, iotDeviceId, handleClose, handleReset }: any) {

  const {
    control,
    handleSubmit
  } = useForm({
    resolver: yupResolver(powerSchema),
    defaultValues: {
      lowBatteryLevel: info.lowBatteryLevel || 1
    }
  });

  const [ power, setPower ] = useState(info.powerType);

  const [ message, setMessage ] = useState(null);

  const onSubmit = async (data: { lowBatteryLevel?: number }) => {
    updatePower(iotDeviceId, power, power === 'COMMERCIAL' ? null : data.lowBatteryLevel)
      .then(() => {
        handleReset();
        handleClose();
      })
      .catch(() => setMessage('정보를 수정할 수 없습니다. 나중에 다시 시도해 주세요.'));
  };

  return (
    <form onSubmit={ handleSubmit(onSubmit) } noValidate>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        height: '465px'
      }}>
        <div>
          <Label><span>* </span>전원</Label>
          <select value={power} onChange={(event) => setPower(event.target.value)} disabled={info.enabled ? false : true}>
            <option value='COMMERCIAL'>상시 전원</option>
            <option value='BATTERY'>배터리</option>
          </select>
        </div>
        
        <TextField
          type='number'
          name='lowBatteryLevel'
          control={control}
          label='배터리 부족 경고 (%)'
          min={1}
          max={100}
          disabled={!info.enabled ? true : power === 'COMMERCIAL' ? true : false}
        />
      </div>

      <Message $visible={message ? true : false}>{ message }</Message>

      <ButtonWrapper>
        <PrimaryButton disabled={info.enabled ? false : true}>저장</PrimaryButton>
        <TertiaryButton type='button' onClick={() => handleClose()}>취소</TertiaryButton>
      </ButtonWrapper>
    </form>
  );
}

// ----------------------------------------------------------------------

const actionSchema = yup.object().shape({
  measurementCycle: yup.string()
    .required(),
  airCompressorActionCycle: yup.string()
    .required(),
  airCompressorSprayTime: yup.string()
    .required(),
  fanControlTemperature: yup.number()
    .min(1, '1~100 사이 정수를 입력해 주세요.')
    .max(100, '1~100 사이 정수를 입력해 주세요.')
    .integer('1~100 사이 정수를 입력해 주세요.')
});

function Action({ info, iotDeviceId, handleClose, handleReset }: any) {
  const {
    control,
    handleSubmit
  } = useForm({
    resolver: yupResolver(actionSchema),
    defaultValues: {
      measurementCycle: info?.measurementCycle || '5',
      airCompressorActionCycle: info?.airCompressorActionCycle || '2',
      airCompressorSprayTime: info?.airCompressorSprayTime || '20',
      fanControlTemperature: info?.fanControlTemperature || 1
    }
  });

  const [ airCompressorEnable, setAirCompressorEnable ] = useState(info?.airCompressorEnable !== null ? info?.airCompressorEnable : true);
  const [ fanEnable, setFanEnable ] = useState(info?.fanEnable !== null ? info?.fanEnable : true);

  const [ message, setMessage ] = useState(null);

  const onSubmit = async (data: { measurementCycle?: string, airCompressorActionCycle?: string, airCompressorSprayTime?: string, fanControlTemperature?: number }) => {
    const airCompressorActionCycle = airCompressorEnable ? data.airCompressorActionCycle : null;
    const airCompressorSprayTime = airCompressorEnable ? data.airCompressorSprayTime : null;
    const fanControlTemperature = fanEnable ? data.fanControlTemperature : null;

    await updateAction(iotDeviceId, data.measurementCycle, airCompressorEnable, airCompressorActionCycle, airCompressorSprayTime, fanEnable, fanControlTemperature)
      .then(() => {
        handleReset();
        handleClose();
      })
      .catch((error) => {
        if ( error.response.status === 422 ) return setMessage('장비의 응답이 없으므로 정보를 수정할 수 없습니다.');
        setMessage('정보를 수정할 수 없습니다. 나중에 다시 시도해 주세요.');
      });
  };

  return (
    <form onSubmit={ handleSubmit(onSubmit) } noValidate>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        height: '465px'
      }}>
        <p style={{'marginBottom': '10px'}}>설정 변경 시간 : { timezones(info?.settingAt, 'YYYY-MM-DD HH:mm:ss') }</p>

        <SelectField
          name='measurementCycle'
          control={control}
          label='데이터 측정 주기 (분)'
          essential={true}
          option={[
            {value: '5', name: '5'},
            {value: '10', name: '10'},
            {value: '15', name: '15'},
            {value: '20', name: '20'}
          ]}
          disabled={info?.enabled ? false : true}
        />

        <Switch>
          <Label><span>* </span>에어 컴프레서 활성화</Label>
          <label>
            <input type='checkbox' checked={airCompressorEnable} onChange={(event) => setAirCompressorEnable(event.target.checked)} disabled={info?.enabled ? false : true} />
          </label>
        </Switch>

        <SelectField
          name='airCompressorActionCycle'
          control={control}
          label='에어 컴프레서 동작 주기 (시)'
          option={[
            {value: '2', name: '2'},
            {value: '4', name: '4'},
            {value: '6', name: '6'},
            {value: '8', name: '8'},
            {value: '12', name: '12'}
          ]}
          disabled={!info?.enabled ? true : !airCompressorEnable ? true : false}
        />

        <SelectField
          name='airCompressorSprayTime'
          control={control}
          label='에어 컴프레서 분사 주기 (초)'
          option={[
            {value: '20', name: '20'},
            {value: '30', name: '30'},
            {value: '40', name: '40'},
            {value: '50', name: '50'}
          ]}
          disabled={!info?.enabled ? true : !airCompressorEnable ? true : false}
        />

        <Switch>
          <Label><span>* </span>환풍기 활성화</Label>
          <label>
            <input type='checkbox' checked={fanEnable} onChange={(event) => setFanEnable(event.target.checked)} disabled={info?.enabled ? false : true} />
          </label>
        </Switch>
        
        <TextField
          type='number'
          name='fanControlTemperature'
          control={control}
          label='환풍기 제어 온도 (℃)'
          min={1}
          max={100}
          disabled={!info?.enabled ? true : !fanEnable ? true : false}
        />
      </div>

      <Message $visible={message ? true : false}>{ message }</Message>

      <ButtonWrapper>
        <PrimaryButton disabled={info?.enabled ? false : true}>저장</PrimaryButton>
        <TertiaryButton type='button' onClick={() => handleClose()}>취소</TertiaryButton>
      </ButtonWrapper>
    </form>
  );
}